// src/components/admin/EditorConfigCanje.js
// Sección en tab Integraciones para editar la configuración global de
// códigos de canje. Solo SuperAdmin.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Alert, Button, Grid,
  TextField, FormControlLabel, Switch, CircularProgress, Divider, Chip,
} from '@mui/material';
import RedeemIcon from '@mui/icons-material/Redeem';
import SaveIcon from '@mui/icons-material/Save';
import { getConfigCanje, actualizarConfigCanje } from '../../services/codigosCanjeApi';

export default function EditorConfigCanje({ onToast }) {
  const [cfg, setCfg]         = useState(null);
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const c = await getConfigCanje();
      setCfg(c);
    } catch (e) {
      onToast?.(e.message || 'No se pudo cargar la configuración.', 'error');
    } finally { setLoading(false); }
  }, [onToast]);

  useEffect(() => { cargar(); }, [cargar]);

  const guardar = async () => {
    setGuardando(true);
    try {
      const r = await actualizarConfigCanje({
        usosMaximosDefault:                parseInt(cfg.usosMaximosDefault, 10),
        vigenciaDiasDefault:               cfg.vigenciaDiasDefault ? parseInt(cfg.vigenciaDiasDefault, 10) : 0,
        minConsumosParaAprobacion:         parseInt(cfg.minConsumosParaAprobacion, 10),
        montoMinimoAcumuladoParaAprobacion: parseFloat(cfg.montoMinimoAcumuladoParaAprobacion),
        montoMinimoPorConsumo:             cfg.montoMinimoPorConsumo ? parseFloat(cfg.montoMinimoPorConsumo) : 0,
        permitirEmisionAutomatica:         !!cfg.permitirEmisionAutomatica,
        requiereAprobacionAuditor:         !!cfg.requiereAprobacionAuditor,
      });
      onToast?.('Configuración actualizada.', 'success');
      setCfg(r);
    } catch (e) {
      onToast?.(e.message || 'No se pudo guardar.', 'error');
    } finally { setGuardando(false); }
  };

  const set = (k, v) => setCfg(p => ({ ...p, [k]: v }));

  return (
    <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <RedeemIcon sx={{ color: '#2e7d32' }} />
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a4d2e' }}>
          Configuración de códigos de canje
        </Typography>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Parámetros globales del módulo. Los cambios aplican a los códigos nuevos que se emitan
        de aquí en adelante; los códigos existentes conservan su snapshot.
      </Typography>

      {loading && <CircularProgress size={20} />}

      {cfg && !loading && (
        <Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField size="small" fullWidth type="number" label="Usos máximos por código"
                value={cfg.usosMaximosDefault ?? ''}
                onChange={e => set('usosMaximosDefault', e.target.value)}
                helperText="Cuántas compras puede registrar cada código" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField size="small" fullWidth type="number" label="Vigencia (días)"
                value={cfg.vigenciaDiasDefault ?? ''}
                onChange={e => set('vigenciaDiasDefault', e.target.value)}
                helperText="0 o vacío = sin vencimiento" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField size="small" fullWidth type="number" label="Mín. consumos para aprobación"
                value={cfg.minConsumosParaAprobacion ?? ''}
                onChange={e => set('minConsumosParaAprobacion', e.target.value)}
                helperText="Nº mínimo de compras" />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField size="small" fullWidth type="number" label="Monto mínimo acumulado (COP)"
                value={cfg.montoMinimoAcumuladoParaAprobacion ?? ''}
                onChange={e => set('montoMinimoAcumuladoParaAprobacion', e.target.value)}
                helperText="Total en compras para quedar apto a asociarse" />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField size="small" fullWidth type="number" label="Monto mínimo por consumo (COP)"
                value={cfg.montoMinimoPorConsumo ?? ''}
                onChange={e => set('montoMinimoPorConsumo', e.target.value)}
                helperText="Piso por compra individual. 0 o vacío = sin piso" />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }}><Chip label="Flujo de emisión" size="small" /></Divider>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Switch checked={!!cfg.permitirEmisionAutomatica}
                  onChange={e => set('permitirEmisionAutomatica', e.target.checked)} />}
                label={<Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Permitir emisión automática</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Reservado para futura integración en wizard (fase E).
                  </Typography>
                </Box>}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={<Switch checked={!!cfg.requiereAprobacionAuditor}
                  onChange={e => set('requiereAprobacionAuditor', e.target.checked)} />}
                label={<Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Requiere aprobación de auditor</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Si está activo, los códigos nacen en PENDIENTE_APROBACION.
                  </Typography>
                </Box>}
              />
            </Grid>
          </Grid>

          <Alert severity="info" sx={{ mt: 2 }}>
            Regla actual: un candidato queda apto para asociarse cuando cumple <strong>ambas condiciones</strong>:
            (a) tiene al menos <strong>{cfg.minConsumosParaAprobacion || 0}</strong> consumo(s),
            y (b) su monto acumulado es <strong>≥ ${Number(cfg.montoMinimoAcumuladoParaAprobacion || 0).toLocaleString('es-CO')}</strong>.
          </Alert>

          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            <Button variant="contained" color="success" onClick={guardar} disabled={guardando}
              startIcon={guardando ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}>
              Guardar configuración
            </Button>
            <Button onClick={cargar} disabled={guardando}>Descartar</Button>
          </Stack>
        </Box>
      )}
    </Paper>
  );
}
